import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Truck, Radio, Wifi, LayoutDashboard, BrainCircuit, MonitorDot, UserCheck } from "lucide-react";

const NODES = [
  { Icon: Truck, label: "Vehicles", meta: "Trucks · Buses · Vans · Assets" },
  { Icon: Radio, label: "IoT Sensors", meta: "GPS · Telematics · Cameras" },
  { Icon: Wifi, label: "Connectivity", meta: "4G / 5G · IoT · Cloud" },
  { Icon: LayoutDashboard, label: "Fleet Platform", meta: "Tracking · Dispatch · Routing" },
  { Icon: BrainCircuit, label: "AI", meta: "Video analytics · Prediction" },
  { Icon: MonitorDot, label: "Operations", meta: "Control room · Dispatchers" },
  { Icon: UserCheck, label: "Customer", meta: "Notifications · Visibility" },
];

export const EcosystemFlow = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ perspective: 1200 }} data-testid="hero-ecosystem-flow" className="relative mx-auto max-w-md lg:ml-auto">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative rounded-3xl border border-line bg-navy-800/60 p-6 backdrop-blur-xl shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)]">
        <div className="flex items-center justify-between border-b border-line pb-4">
          <span className="eyebrow">Connected Transportation Ecosystem</span>
          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live
          </span>
        </div>

        <div className="relative mt-5">
          <div className="absolute left-[22px] top-4 bottom-4 w-px bg-cyan-400/25">
            <motion.span
              className="absolute left-1/2 h-16 w-[3px] -translate-x-1/2 rounded-full bg-gradient-to-b from-transparent via-cyan-400 to-transparent"
              animate={{ top: ["-10%", "100%"] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "linear", delay: 1 }}
            />
          </div>
          <ul className="relative space-y-3">
            {NODES.map(({ Icon, label, meta }, i) => (
              <motion.li
                key={label}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="group flex items-center gap-4 rounded-xl border border-transparent px-1.5 py-1.5 transition-colors hover:border-line hover:bg-white/[0.03]"
              >
                <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line-strong bg-navy text-cyan-400 transition-[box-shadow] group-hover:glow-dot">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-sm font-medium text-white">{label}</div>
                  <div className="truncate font-mono text-[10px] uppercase tracking-wider text-slate-500">{meta}</div>
                </div>
                <span className="font-mono text-[10px] text-slate-600">0{i + 1}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </motion.div>
      <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-cyan-400/15 to-brand-blue/15 blur-3xl" />
    </div>
  );
};
